import { useEffect, useState } from "react";
import { Modal } from "@/ui/Modal";
import { PopupSizes } from "@/ui/Modal/types";
import { publishPage } from "@/api/pages";
import { publishCampaign } from "@/api/campaigns";
import { getSessionToken } from "@/api/auth";

// AMB-698 — publish, Launch Pages style: one click, no review step (Kyle, Oct 7).
// Publishes right away, then shows where each page went live. Tracking from the site's
// Scripts & Tracking (and the page's own) is added silently when set. The only extra note
// is when a page already carries the same pixel (visits would count twice).
// Used for one page, or for "Publish Website" (every HTML page, then the drag-and-drop
// pages through the existing builder publish).

type Target = { id: string; name: string; slug: string };

export const PublishHtmlModal = ({
  pages,
  siteAddress,
  campaignId,
  builderPageCount = 0,
  setIsOpen,
  onPublished,
}: {
  pages: Target[];
  siteAddress?: string;
  campaignId?: string; // set for "Publish Website" so builder pages publish too
  builderPageCount?: number;
  setIsOpen: (open: boolean) => void;
  onPublished: () => void;
}) => {
  const token = getSessionToken() || "";
  const [stage, setStage] = useState<"publishing" | "done">("publishing");
  const [results, setResults] = useState<{ name: string; ok: boolean; text: string; note?: string }[]>([]);

  const where = (slug: string) => (siteAddress ? `${siteAddress}/${slug}` : `/${slug}`);
  const withBuilder = Boolean(campaignId && builderPageCount > 0);

  useEffect(() => {
    (async () => {
      const out: { name: string; ok: boolean; text: string; note?: string }[] = [];
      for (const p of pages) {
        const r = await publishPage(p.id, { token });
        const ok = Boolean(r && r.ok !== false && !r.error);
        const dup = ok && r.duplicateTracking?.length
          ? `This page already has ${r.duplicateTracking.join(", ")}, which the site also adds, so visits may count twice.`
          : undefined;
        out.push({ name: p.name, ok, text: ok ? `Live at ${where(p.slug)}` : r?.error || "Couldn't publish.", note: dup || (ok ? r.note : undefined) });
        setResults([...out]);
      }
      if (withBuilder && campaignId) {
        const r: any = await publishCampaign(campaignId, { token });
        const ok = Boolean(r && !r.error);
        out.push({ name: `${builderPageCount} page${builderPageCount === 1 ? "" : "s"} built in the editor`, ok, text: ok ? "Published" : r?.error || "Couldn't publish." });
        setResults([...out]);
      }
      setStage("done");
    })();
  }, []);

  const row = (mark: string, title: string, detail?: string, warn = false) => (
    <div className="flex gap-3 items-start">
      <span className={`w-5 text-center font-bold ${warn ? "text-yellow-600" : "text-green-600"}`}>{mark}</span>
      <div className="min-w-0">
        <div className="text-sm text-black dark:text-white">{title}</div>
        {detail && <div className="text-xs text-black/50 dark:text-white/50 break-words">{detail}</div>}
      </div>
    </div>
  );

  const total = pages.length + (withBuilder ? builderPageCount : 0);
  const close = () => {
    setIsOpen(false);
    if (stage === "done") onPublished();
  };

  return (
    <Modal
      title={stage === "done" ? (results.every((r) => r.ok) ? "Published" : "Published, with problems") : `Publishing ${total} page${total === 1 ? "" : "s"}…`}
      onClose={close}
      onSuccess={() => stage === "done" && close()}
      successCaption={stage === "done" ? "Done" : "Publishing…"}
      size={PopupSizes.MEDIUM}
    >
      <div className="flex flex-col gap-3 text-left">
        {results.map((r, i) => (
          <div key={i}>
            {row(r.ok ? "✓" : "!", r.name, r.text, !r.ok)}
            {r.ok && r.text.startsWith("Live at ") && (
              <a
                className="ml-8 text-xs underline text-black/60 dark:text-white/60"
                href={`https://${r.text.replace("Live at ", "")}`}
                target="_blank"
                rel="noreferrer"
              >
                Open page
              </a>
            )}
            {r.note && <div className="ml-8 mt-1 text-xs text-yellow-700 dark:text-yellow-500">{r.note}</div>}
          </div>
        ))}
        {stage === "publishing" && <div className="text-sm text-black/50">Publishing…</div>}
      </div>
    </Modal>
  );
};
